import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import '../styles/Home.css';
import logo from '../assets/logo/light.png';
import {
  LayoutDashboard, Key, Webhook, ScrollText, Store, Plug,
  BookOpen, LogOut, ChevronRight,
} from 'lucide-react';


const API_BASE = process.env.REACT_APP_API_BASE;

// ── API helper ───────────────────────────────────
async function apiFetch(endpoint, body, token) {
  const base = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
  const res = await fetch(`${base}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', AUTH: token },
    body: JSON.stringify(body),
  });
  let data;
  const text = await res.text();
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Server error (${res.status})${text ? ': ' + text.slice(0, 120) : ''}`);
  }
  if (!data.success) throw new Error(data.response || 'Request failed');
  return data;
}

function formatDate(ts) {
  return new Date((ts || 0) * 1000).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Mock event data (logs / overview preview) ────


const PAYLOAD_NEW = `{
  "event": "order.new",
  "data": {
    "merchant_token": "downtown",
    "order": {
      "id":      489734,
      "status":  "pending",
      "type":    "delivery",
      "when":    "now",
      "payment": { "method": "cash", "amount": 600 },
      "customer": {
        "name":  "John Doe",
        "phone": "+5978123456"
      },
      "delivery": {
        "address":  "Henck Arronstraat 12",
        "coords":   { "lat": 5.8332, "lng": -55.1668 },
        "comments": "Ring the bell",
        "fee": 80
      },
      "items": [
        {
          "title":   "Chicken Burger",
          "variant": "Large",
          "qty":     2,
          "total":   140
        }
      ],
      "totals": {
        "curr": "SRD",
        "order": 420, "delivery": 80, "total": 521
      }
    }
  }
}`;

const PAYLOAD_PUSH = `POST https://ride-eats.com/api2/sync/order/push
Authorization: re_live_a3f8c2...

{
  "id":     489734,
  "status": "accepted",
  "data":   { "time": 20 }
}

// 200 OK
{
  "success": true,
  "data": {
    "order_id":        489734,
    "previous_status": "pending",
    "new_status":      "accepted"
  }
}`;

// ── Helpers ─────────────────────────────────────
function StatusPill({ status }) {
  return <span className={`status-pill sp-${status}`}>{status}</span>;
}

function timeAgo(ts) {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

function EventTable({ events }) {
  if (!events || events.length === 0) {
    return <div className="empty-state" style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13.5 }}>No events yet. Events appear here once your webhooks start receiving orders.</div>;
  }
  return (
    <table className="event-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          <th>Event</th>
          <th>Order ID</th>
          <th>Merchant</th>
          <th>Status</th>
          <th>Type</th>
          <th>Time</th>
        </tr>
      </thead>
      <tbody>
        {events.map(e => (
          <tr key={e.id}>
            <td><span className={`event-type ${e.event === 'order.new' ? 'new' : 'update'}`}>{e.event}</span></td>
            <td><span className="event-order-id">#{e.order_id}</span></td>
            <td><span className="merchant-token">{e.merchant_token}</span></td>
            <td><StatusPill status={e.status} /></td>
            <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{e.type}</td>
            <td><span className="event-time">{timeAgo(e.timestamp)}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Snippet({ lang, code }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <div className="snippet-block">
      <div className="snippet-header">
        <span className="snippet-lang">{lang}</span>
        <button className="snippet-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre>{code}</pre>
    </div>
  );
}

// ── Form Modal (reusable overlay with form content) ─
function FormModal({ title, onClose, onSubmit, submitLabel = 'Save', children }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">{title}</h2>
        <div className="modal-content">
          <div className="modal-form-row">{children}</div>
        </div>
        <div className="modal-actions">
          <button className="modal-btn modal-btn--confirm" onClick={onSubmit}>
            {submitLabel}
          </button>
          <button className="modal-btn modal-btn--cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Loading / Error states ────────────────────────
function LoadingState({ rows = 3 }) {
  return (
    <div className="dash-content">
      <div className="loading-rows">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="loading-row" />
        ))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="dash-content">
      <div className="error-state">
        <span>{message || 'Failed to load data.'}</span>
        {onRetry && <button className="btn-sm" style={{ marginLeft: 12 }} onClick={onRetry}>Retry</button>}
      </div>
    </div>
  );
}

// ── Pages ────────────────────────────────────────
function OverviewPage({ setPage, keys, merchants, logs }) {
  const liveCount = keys.filter(k => k.env === 'live').length;
  const testCount = keys.filter(k => k.env === 'test').length;
  const webhookActive = keys.length > 0;
  return (
    <div className="dash-content">

      <div className="stats-row">
        <div className="stat-item">
          <span className="stat-label">Merchants</span>
          <span className="stat-value">{merchants.length}</span>
          <span className="stat-sub">{merchants.filter(m => m.token).map(m => m.token).join(' · ') || '—'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">API Keys</span>
          <span className="stat-value">{keys.length}</span>
          <span className="stat-sub">{liveCount} live · {testCount} test</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Events today</span>
          <span className="stat-value">{logs.filter(e => e.timestamp > Math.floor(Date.now()/1000) - 86400).length}</span>
          <span className="stat-sub">{logs.filter(e => e.event === 'order.new' && e.timestamp > Math.floor(Date.now()/1000) - 86400).length} new · {logs.filter(e => e.event === 'order.update' && e.timestamp > Math.floor(Date.now()/1000) - 86400).length} updates</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Webhook</span>
          <span className={`stat-value ${webhookActive ? 'ov-stat-active' : ''}`}>{webhookActive ? 'Active' : 'Inactive'}</span>
          <span className="stat-sub">{logs.length} total events logged</span>
        </div>
      </div>

      <div className="qa-grid">
        <button className="qa-card" onClick={() => setPage('api-keys')}>
          <Key size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">API Keys</div>
            <div className="qa-card-sub">{keys.length} active, {merchants.length} merchants</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
        <button className="qa-card" onClick={() => setPage('webhooks')}>
          <Webhook size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">Webhooks</div>
            <div className="qa-card-sub">Per-key callback URLs</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
        <button className="qa-card" onClick={() => setPage('merchants')}>
          <Store size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">Merchants</div>
            <div className="qa-card-sub">{merchants.length} locations linked to account</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
        <button className="qa-card" onClick={() => setPage('logs')}>
          <ScrollText size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">Event Logs</div>
            <div className="qa-card-sub">{logs.length} total events logged</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
      </div>

      <div className="event-feed">
        <div className="section-title">Recent events</div>
        <EventTable events={logs.slice(0, 6)} />
      </div>

    </div>
  );
}

function ApiKeysPage({ keys, setKeys, merchants, token }) {
  const { openModal } = useModal();
  const [revealed,       setRevealed]       = useState({});
  const [copied,         setCopied]         = useState({});
  const [toggling,       setToggling]       = useState({});
  const [showNewKey,     setShowNewKey]     = useState(false);
  const [newName,        setNewName]        = useState('');
  const [newEnv,         setNewEnv]         = useState('live');
  const [newMerchants,   setNewMerchants]   = useState([]);
  const [creating,       setCreating]       = useState(false);
  const [createError,    setCreateError]    = useState('');
  const [editMerchKey,   setEditMerchKey]   = useState(null); // key object being edited
  const [editMerchSel,   setEditMerchSel]   = useState([]);
  const [savingMerch,    setSavingMerch]    = useState(false);
  const [saveMerchErr,   setSaveMerchErr]   = useState('');

  function toggleReveal(id) { setRevealed(r => ({ ...r, [id]: !r[id] })); }

  function copyKey(id, val) {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(c => ({ ...c, [id]: true }));
      setTimeout(() => setCopied(c => ({ ...c, [id]: false })), 2000);
    });
  }

  async function doToggle(k) {
    const next = !k.enabled;
    setToggling(t => ({ ...t, [k.id]: true }));
    try {
      await apiFetch('dev/key/toggle', { key_id: k.id, enabled: next ? 1 : 0 }, token);
      setKeys(ks => ks.map(x => x.id === k.id ? { ...x, enabled: next } : x));
    } catch (err) {
      openModal({ title: 'Error', text: err.message, cancel: { label: 'OK' } });
    } finally {
      setToggling(t => ({ ...t, [k.id]: false }));
    }
  }

  async function doDelete(key_id) {
    try {
      await apiFetch('dev/key/delete', { key_id }, token);
      setKeys(ks => ks.filter(k => k.id !== key_id));
    } catch (err) {
      openModal({ title: 'Error', text: err.message, cancel: { label: 'OK' } });
    }
  }

  function confirmDelete(k) {
    openModal({
      title: 'Delete API key',
      text: `"${k.name}" will be permanently removed. Any integration using this key will stop working immediately.`,
      confirm: { label: 'Delete key', danger: true, onClick: () => doDelete(k.id) },
      cancel:  { label: 'Keep it' },
    });
  }

  function openNewKey() {
    setNewName(''); setNewEnv('live'); setNewMerchants([]); setCreateError(''); setShowNewKey(true);
  }

  function toggleNewMerchant(t) {
    setNewMerchants(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }

  async function createKey() {
    if (!newName.trim()) { setCreateError('Key name is required.'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const res = await apiFetch('dev/key/create', {
        name: newName.trim(),
        env:  newEnv,
        merchants: newMerchants,
      }, token);
      setKeys(ks => [res.data.key, ...ks]);
      setShowNewKey(false);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  function openEditMerchants(k) {
    setEditMerchKey(k);
    setEditMerchSel(k.merchants ? [...k.merchants] : []);
    setSaveMerchErr('');
  }

  function toggleEditMerch(t) {
    setEditMerchSel(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }

  async function saveEditMerchants() {
    if (!editMerchKey) return;
    setSavingMerch(true);
    setSaveMerchErr('');
    try {
      const res = await apiFetch('dev/key/merchants/update', {
        key_id:    editMerchKey.id,
        merchants: editMerchSel,
      }, token);
      setKeys(ks => ks.map(k => k.id === editMerchKey.id ? { ...k, merchants: res.data.merchants } : k));
      setEditMerchKey(null);
    } catch (err) {
      setSaveMerchErr(err.message);
    } finally {
      setSavingMerch(false);
    }
  }

  const configuredMerchants = (merchants || []).filter(m => m.token);

  return (
    <div className="dash-content">
      {showNewKey && (
        <FormModal
          title="New API key"
          onClose={() => setShowNewKey(false)}
          onSubmit={createKey}
          submitLabel={creating ? 'Creating…' : 'Create key'}
        >
          {createError && <div className="form-error">{createError}</div>}
          <div>
            <label className="field-label">Key name</label>
            <input
              className="field-input"
              placeholder="e.g. KFC Kwatta POS"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Environment</label>
            <select className="dash-select" value={newEnv} onChange={e => setNewEnv(e.target.value)}>
              <option value="live">Live</option>
              <option value="test">Test</option>
            </select>
          </div>
          {configuredMerchants.length > 0 && (
            <div>
              <label className="field-label">Linked merchants</label>
              <div className="merchant-check-list">
                {configuredMerchants.map(m => (
                  <label key={m.id} className="merchant-check-item">
                    <input
                      type="checkbox"
                      checked={newMerchants.includes(m.token)}
                      onChange={() => toggleNewMerchant(m.token)}
                    />
                    <span className="merchant-token">{m.token}</span>
                    <span className="merchant-check-name">{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {configuredMerchants.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 0' }}>
              No merchant tokens configured yet. Set up tokens in the Merchants tab first.
            </p>
          )}
        </FormModal>
      )}

      {editMerchKey !== null && (
        <FormModal
          title={`Merchants — ${editMerchKey.name}`}
          onClose={() => setEditMerchKey(null)}
          onSubmit={saveEditMerchants}
          submitLabel={savingMerch ? 'Saving…' : 'Save'}
        >
          {saveMerchErr && <div className="form-error">{saveMerchErr}</div>}
          {configuredMerchants.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              No merchant tokens configured yet. Go to the <strong style={{ color: 'var(--text)' }}>Merchants</strong> tab and set up tokens first.
            </p>
          ) : (
            <div>
              <label className="field-label">Linked merchants</label>
              <div className="merchant-check-list">
                {configuredMerchants.map(m => (
                  <label key={m.id} className="merchant-check-item">
                    <input
                      type="checkbox"
                      checked={editMerchSel.includes(m.token)}
                      onChange={() => toggleEditMerch(m.token)}
                    />
                    <span className="merchant-token">{m.token}</span>
                    <span className="merchant-check-name">{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </FormModal>
      )}

      <div className="section-header-row">
        <div className="section-title">API Keys</div>
        <button className="btn-sm accent" onClick={openNewKey}>+ New key</button>
      </div>

      {keys.length === 0 && (
        <div className="empty-state">
          <h3>No API keys yet</h3>
          <p>Create your first key to start integrating with the Ride Eats platform.</p>
        </div>
      )}

      <div className="key-list">
        {keys.map(k => (
          <div className={`key-card${k.enabled ? '' : ' key-card--disabled'}`} key={k.id}>
            <div className="key-card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="key-card-name">{k.name}</span>
                <span className={`key-env-badge ${k.env === 'live' ? 'live' : 'test'}`}>{k.env}</span>
                {!k.enabled && <span className="key-disabled-badge">Disabled</span>}
              </div>
              <div className="key-card-actions">
                <button
                  className={`key-toggle-btn ${k.enabled ? 'enabled' : 'off'}`}
                  onClick={() => doToggle(k)}
                  disabled={!!toggling[k.id]}
                >
                  {toggling[k.id] ? '…' : k.enabled ? 'Disable' : 'Enable'}
                </button>
                <button className="btn-sm" onClick={() => openEditMerchants(k)}>Merchants</button>
                <button className="key-delete-btn" onClick={() => confirmDelete(k)}>Delete</button>
              </div>
            </div>
            <div className="key-value-block">
              <span className={`key-value-text${revealed[k.id] ? '' : ' masked'}`}>
                {revealed[k.id] ? k.value : k.value.slice(0, 8) + '•'.repeat(24)}
              </span>
              <div className="key-value-actions">
                <button className="key-copy-btn" onClick={() => toggleReveal(k.id)}>
                  {revealed[k.id] ? 'Hide' : 'Reveal'}
                </button>
                <button
                  className={`key-copy-btn${copied[k.id] ? ' copied' : ''}`}
                  onClick={() => copyKey(k.id, k.value)}
                >
                  {copied[k.id] ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="key-meta-row">
              <div className="key-meta-item">
                <span className="key-meta-label">Created</span>
                <span className="key-meta-value">{formatDate(k.created)}</span>
              </div>
              <div className="key-meta-item">
                <span className="key-meta-label">Merchants</span>
                <span className="key-meta-value">{k.merchants.length > 0 ? k.merchants.join(' · ') : '—'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 40 }}>
        <div className="section-title">How to authenticate</div>
        <Snippet lang="HTTP" code={`POST https://ride-eats.com/api2/sync/order/info\nContent-Type: application/json\nx-api-key: re_live_a3f8c2...\n\n{ "id": 489734 }`} />
      </div>
    </div>
  );
}

function WebhooksPage({ keys, webhookConfigs, setWebhookConfigs, token }) {
  const [selectedKeyId, setSelectedKeyId] = useState(() => keys[0]?.id ?? null);
  const [saved,  setSaved]  = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const activeId = (keys.find(k => k.id === selectedKeyId) ? selectedKeyId : keys[0]?.id) ?? null;
  const cfg = webhookConfigs[activeId] || { urlNew: '', urlUpdate: '' };
  const anyConfigured = cfg.urlNew || cfg.urlUpdate;

  function setUrl(field, value) {
    if (activeId === null) return;
    setSaveErr('');
    setWebhookConfigs(prev => ({
      ...prev,
      [activeId]: { ...cfg, [field]: value },
    }));
  }

  async function save(e) {
    e.preventDefault();
    if (activeId === null) return;
    setSaving(true);
    setSaveErr('');
    try {
      await apiFetch('dev/webhook/save', {
        key_id:     activeId,
        url_new:    cfg.urlNew,
        url_update: cfg.urlUpdate,
      }, token);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveErr(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dash-content">
      <div className="webhook-section">
        <div className="section-header-row" >
          <div className="section-title">Callback URLs</div>
          {keys.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Create an API key first</span>
          )}
          {keys.length === 1 && (
            <span className={`key-env-badge ${keys[0].env === 'live' ? 'live' : 'test'}`}>{keys[0].name}</span>
          )}
          {keys.length > 1 && (
            <select
              className="dash-select"
              value={activeId ?? ''}
              onChange={e => setSelectedKeyId(Number(e.target.value))}
            >
              {keys.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.env})</option>
              ))}
            </select>
          )}
        </div>
        <div className="webhook-status-row">
          <div className={`wh-status-dot ${anyConfigured ? 'active' : 'inactive'}`} />
          <span className="wh-status-label">{anyConfigured ? 'Endpoints configured, receiving events' : 'No endpoints set for this key'}</span>
        </div>
        <form className="webhook-form" onSubmit={save}>
          <div>
            <label className="field-label">New order callback URL <span style={{ color: 'var(--accent2)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(order.new)</span></label>
            <input
              className="field-input"
              type="url"
              value={cfg.urlNew}
              onChange={e => setUrl('urlNew', e.target.value)}
              placeholder="https://your-system.com/webhooks/new-order"
              disabled={activeId === null}
            />
          </div>
          <div>
            <label className="field-label">Order update callback URL <span style={{ color: 'var(--yellow)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(order.update)</span></label>
            <input
              className="field-input"
              type="url"
              value={cfg.urlUpdate}
              onChange={e => setUrl('urlUpdate', e.target.value)}
              placeholder="https://your-system.com/webhooks/order-update"
              disabled={activeId === null}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="submit" className="btn-sm accent" disabled={activeId === null || saving}>
              {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save endpoints'}
            </button>
            {saveErr && <span style={{ fontSize: 12, color: '#f87171' }}>{saveErr}</span>}
          </div>
        </form>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div className="section-title">Events sent to your endpoint</div>
        <table className="event-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr><th>Event type</th><th>When fired</th><th>Payload</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><span className="event-type new">order.new</span></td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>Customer places an order</td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>Full order object (items, customer, totals, delivery)</td>
            </tr>
            <tr>
              <td><span className="event-type update">order.update</span></td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>Any status change (by you, biker, or platform)</td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>order_id, previous_status, new_status, extras</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="two-col">
        <div>
          <div className="section-title">order.new payload</div>
          <Snippet lang="JSON" code={PAYLOAD_NEW} />
        </div>
        <div>
          <div className="section-title">order.update payload</div>
          <Snippet lang="JSON" code={`{
  "event": "order.update",
  "data": {
    "merchant_token":  "downtown",
    "order_id":        489734,
    "previous_status": "pending",
    "new_status":      "accepted",
    "status_updated":  1745413000,
    "extras": {
      "time": 20
    }
  }
}`} />
        </div>
      </div>
    </div>
  );
}

function LogsPage({ logs }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all'
    ? logs
    : logs.filter(e => e.event === filter);

  return (
    <div className="dash-content">
      <div className="logs-filter-row">
        {['all', 'order.new', 'order.update'].map(f => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All events' : f}
          </button>
        ))}
      </div>
      <EventTable events={filtered} />
    </div>
  );
}

function EndpointsPage() {
  return (
    <div className="dash-content">
      <div style={{ marginBottom: 40 }}>
        <div className="section-title">POST /order/info</div>
        <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginBottom: 16 }}>
          Fetch full details for one or multiple orders. Orders must belong to a merchant linked to your API key.
        </p>
        <div className="two-col">
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Single request</div>
            <Snippet lang="JSON" code={`{ "id": 489734 }`} />
            <div className="section-title" style={{ marginTop: 20, marginBottom: 8 }}>Batch request (max 50)</div>
            <Snippet lang="JSON" code={`{ "ids": [489734, 489733, 489732] }`} />
          </div>
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Response</div>
            <Snippet lang="JSON" code={`{
  "success":  true,
  "response": "Order info loaded.",
  "data": {
    "merchant_token": "downtown",
    "order": {
      "id":             489734,
      "placed_on":      1745412125,
      "status":         "accepted",
      "status_updated": 1745412300,
      "type":           "delivery",
      "when":           "now",
      "payment":  { "method": "cash", "amount": 600 },
      "customer": { "name": "John Doe", "phone": "+5978..." },
      "delivery": {
        "address":  "Henck Arronstraat 12",
        "coords":   { "lat": 5.8332, "lng": -55.1668 },
        "comments": "Ring the bell",
        "fee": 80
      },
      "biker": null,
      "items": [ { "title": "Chicken Burger", ... } ],
      "totals": {
        "curr": "SRD",
        "order": 420, "delivery": 80, "total": 521
      }
    }
  }
}`} />
          </div>
        </div>
      </div>

      <div>
        <div className="section-title">POST /order/push</div>
        <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginBottom: 16 }}>
          Advance an order through its status lifecycle. Transitions are strictly enforced; out-of-order pushes are rejected.
        </p>
        <div className="two-col">
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Allowed values</div>
            <table className="event-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th>Send</th><th>Use when</th></tr></thead>
              <tbody>
                <tr><td><StatusPill status="accepted"/></td><td style={{fontSize:'13px',color:'var(--muted)'}}>You confirm the order</td></tr>
                <tr><td><span className="status-pill sp-ready">ready</span></td><td style={{fontSize:'13px',color:'var(--muted)'}}>Food is ready (delivery or pickup)</td></tr>
                <tr><td><span className="status-pill sp-ontheway">ontheway</span></td><td style={{fontSize:'13px',color:'var(--muted)'}}>Biker picked up (delivery only)</td></tr>
                <tr><td><StatusPill status="completed"/></td><td style={{fontSize:'13px',color:'var(--muted)'}}>Order delivered or picked up</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Request & response</div>
            <Snippet lang="JSON" code={PAYLOAD_PUSH} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MerchantsPage({ merchants, setMerchants, token }) {
  const [search,     setSearch]     = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState('');

  function openTokenModal(m) {
    setTokenInput(m.token || '');
    setSaveError('');
    setEditTarget(m.id);
  }

  async function saveToken() {
    const t = tokenInput.trim().toLowerCase();
    if (!t) { setSaveError('Token is required.'); return; }
    if (!/^[a-z0-9][a-z0-9_-]{1,63}$/.test(t)) {
      setSaveError('2–64 chars: lowercase letters, numbers, hyphens, underscores.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await apiFetch('dev/merchant/token/set', { place_id: editTarget, token: t }, token);
      setMerchants(ms => ms.map(m => m.id === editTarget ? { ...m, token: t } : m));
      setEditTarget(null);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const editMerchant = merchants.find(m => m.id === editTarget);

  return (
    <div className="dash-content">
      {editTarget !== null && (
        <FormModal
          title={editMerchant?.token ? 'Edit merchant token' : 'Set up merchant token'}
          onClose={() => setEditTarget(null)}
          onSubmit={saveToken}
          submitLabel={saving ? 'Saving…' : 'Save token'}
        >
          {saveError && <div className="form-error">{saveError}</div>}
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 10px' }}>
            <strong style={{ color: 'var(--text)' }}>{editMerchant?.name}</strong> — this token identifies the merchant in every webhook payload.
          </p>
          <div>
            <label className="field-label">Token</label>
            <input
              className="field-input"
              placeholder="e.g. kfc-kwatta"
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value.toLowerCase())}
              autoFocus
            />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
              Lowercase letters, numbers, hyphens, underscores. 2–64 characters.
            </div>
          </div>
        </FormModal>
      )}

      <div className="section-header-row">
        <div className="section-title">Merchants</div>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Synced from your Ride Eats account</span>
      </div>

      <input
        className="field-input merchants-search"
        placeholder="Search by name or token…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 16, maxWidth: 320 }}
      />

      <table className="event-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th>Merchant</th>
            <th>Token</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {merchants.filter(m => {
            if (!search.trim()) return true;
            const q = search.trim().toLowerCase();
            return m.name.toLowerCase().includes(q) || (m.token || '').toLowerCase().includes(q);
          }).map(m => (
            <tr key={m.id}>
              <td style={{ fontSize: 13.5, color: 'var(--text)', fontWeight: 500 }}>{m.name}</td>
              <td>
                {m.token
                  ? <span className="merchant-token">{m.token}</span>
                  : <span className="setup-badge">Not configured</span>
                }
              </td>
              <td>
                {m.token
                  ? <span className="status-pill sp-active">Active</span>
                  : <span className="status-pill sp-inactive">Inactive</span>
                }
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="btn-sm" onClick={() => openTokenModal(m)}>
                    {m.token ? 'Edit token' : 'Set up'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ marginTop: 24, fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
        Merchant tokens appear as <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent2)' }}>merchant_token</code> in every webhook payload. Each merchant without a token will not receive webhooks and cannot be linked to new API keys.
      </p>
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────
export default function Home() {
  const [page, setPage] = useState('overview');
  const { token, account, logout } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [signingOut, setSigningOut] = useState(false);

  // ── Shared state loaded from API ─────────────────────────────
  const [keys,           setKeys]           = useState(null);
  const [merchants,      setMerchants]      = useState(null);
  const [webhookConfigs, setWebhookConfigs] = useState({});
  const [logs,           setLogs]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(null);

  function loadDashboard() {
    setLoading(true);
    setLoadError(null);
    Promise.all([
      apiFetch('dev/merchants', {}, token),
      apiFetch('dev/keys',      {}, token),
      apiFetch('dev/webhooks',  {}, token),
      apiFetch('dev/logs',      {}, token),
    ]).then(([mData, kData, wData, lData]) => {
      setMerchants(mData.data.merchants);
      setKeys(kData.data.keys);
      setWebhookConfigs(wData.data.webhooks || {});
      setLogs(lData.data.logs || []);
      setLoading(false);
    }).catch(err => {
      setLoadError(err.message);
      setLoading(false);
    });
  }

  useEffect(() => { loadDashboard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function doSignOut() {
    setSigningOut(true);
    try {
      await fetch(`${API_BASE}/auth/sign_out`, {
        method: 'POST',
        headers: { AUTH: token },
      });
    } catch { /* ignore network errors — still log out locally */ }
    logout();
    navigate('/login');
  }

  function handleSignOut() {
    openModal({
      title: 'Sign out',
      text: 'Are you sure you want to sign out?',
      confirm: { label: 'Sign out', danger: true, onClick: doSignOut },
      cancel: { label: 'Stay signed in' },
    });
  }

  const PAGE_TITLES = {
    overview:   'Overview',
    'api-keys': 'API Keys',
    webhooks:   'Webhooks',
    logs:       'Event Logs',
    merchants:  'Merchants',
    endpoints:  'Endpoints',
  };

  return (
    <div className="dash-layout">
      <aside className="sidebar">
        <Link to="/" className="sidebar-brand" style={{ textDecoration: 'none' }}>
          <img src={logo} alt="" />
          <div>
            <div className="sidebar-brand-text">Ride Eats</div>
            <div className="sidebar-brand-sub">Developers</div>
          </div>
        </Link>

        <nav className="sidebar-nav">
          <div>
            <div className="sidebar-section-label">Dashboard</div>
            <a
              href="#overview"
              className={`sidebar-item${page === 'overview' ? ' active' : ''}`}
              style={{ textDecoration: 'none' }}
              onClick={e => { e.preventDefault(); setPage('overview'); }}
            >
              <LayoutDashboard size={14} className="sicon-svg" />Overview
            </a>
          </div>

          <div>
            <div className="sidebar-section-label">Keys &amp; Events</div>
            <a href="#api-keys" className={`sidebar-item${page === 'api-keys' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); setPage('api-keys'); }}>
              <Key size={14} className="sicon-svg" />API Keys
            </a>
            <a href="#webhooks" className={`sidebar-item${page === 'webhooks' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); setPage('webhooks'); }}>
              <Webhook size={14} className="sicon-svg" />Webhooks
            </a>
            <a href="#logs" className={`sidebar-item${page === 'logs' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); setPage('logs'); }}>
              <ScrollText size={14} className="sicon-svg" />Event Logs
            </a>
          </div>

          <div>
            <div className="sidebar-section-label">Config</div>
            <a href="#merchants" className={`sidebar-item${page === 'merchants' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); setPage('merchants'); }}>
              <Store size={14} className="sicon-svg" />Merchants
            </a>
            <a href="#endpoints" className={`sidebar-item${page === 'endpoints' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); setPage('endpoints'); }}>
              <Plug size={14} className="sicon-svg" />Endpoints
            </a>
          </div>
        </nav>

        <div className="sidebar-footer">
          <Link to="/docs" className="sidebar-item" style={{ textDecoration: 'none' }}>
            <BookOpen size={14} className="sicon-svg" />API Reference
          </Link>
        </div>
      </aside>

      <div className="dash-main">
        <div className="dash-topbar">
          <span className="dash-page-title">{PAGE_TITLES[page]}</span>
          <div className="topbar-actions">
            <button className="btn-sm">v1.0</button>
            <div className="topbar-user">
              <span className="topbar-user-name">{account?.name || 'Account'}</span>
              <button className="topbar-signout" onClick={handleSignOut} disabled={signingOut}>
                {signingOut ? 'Signing out…' : <><LogOut size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Sign out</>}
              </button>
            </div>
          </div>
        </div>

        {page === 'endpoints' && <EndpointsPage />}
        {(page === 'overview' || page === 'api-keys' || page === 'webhooks' || page === 'merchants' || page === 'logs') && (
          loading
            ? <LoadingState rows={4} />
            : loadError
              ? <ErrorState message={loadError} onRetry={loadDashboard} />
              : <>
                  {page === 'overview'  && <OverviewPage setPage={setPage} keys={keys} merchants={merchants} logs={logs} />}
                  {page === 'api-keys'  && <ApiKeysPage keys={keys} setKeys={setKeys} merchants={merchants} token={token} />}
                  {page === 'webhooks'  && <WebhooksPage keys={keys} webhookConfigs={webhookConfigs} setWebhookConfigs={setWebhookConfigs} token={token} />}
                  {page === 'merchants' && <MerchantsPage merchants={merchants} setMerchants={setMerchants} token={token} />}
                  {page === 'logs'      && <LogsPage logs={logs} />}
                </>
        )}
      </div>
    </div>
  );
}
